import BigNumber from 'bignumber.js';
import ItemSummary from '@/models/entities/items/item-summary';
import ItemDetail from '@/models/entities/items/item-detail';
import TypedDefaultPk from '@/models/entities/typed-default-pk';
import { Translate } from '@/types/translate';
import { Configuration } from '@/types/configuration.d';
import { PRODUCT_AVAILABILITY_STATUS } from '@/constants/AppConstants';

export function isAdditionalScanCode(item: ItemSummary): boolean {
  try {
    const id = new TypedDefaultPk(item.id);
    return id.isAdditionalScanCode();
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function isSoldByWeight(item: ItemSummary): boolean {
  if (isAdditionalScanCode(item)) {
    return false;
  }
  return Boolean(item.weightProfile);
}

export function isSoldByAverageWeight(item: ItemSummary): boolean {
  if (isAdditionalScanCode(item)) {
    return false;
  }
  return item.averageWeight >= 0;
}

export function isSoldByRandomWeight(item: ItemSummary): boolean {
  return isSoldByWeight(item) && !isSoldByAverageWeight(item);
}

export function isWeightAndQuantityCustomizable(item: ItemSummary): boolean {
  return item.weightProfile?.allowWeightedQuantity;
}

export function hasWeightAbbreviation(item: ItemSummary): boolean {
  return isSoldByWeight(item) && Boolean(item.weightProfile.abbrv);
}

export function isPriceDivided(item: ItemSummary): boolean {
  return Boolean(item.actualPriceDivider) && item.actualPriceDivider > 1;
}

export function unitPrice(item: ItemSummary): BigNumber {
  const actualPriceDivider = isPriceDivided(item) ? item.actualPriceDivider : 1;
  return new BigNumber(item.actualPrice).dividedBy(new BigNumber(actualPriceDivider));
}

export function suggestedPrice(item: ItemSummary): BigNumber {
  return new BigNumber(item.suggestedPrice);
}

export function actualPrice(item: ItemSummary): BigNumber {
  return new BigNumber(item.actualPrice);
}

export function hasActualPrice(item: ItemSummary): boolean {
  return actualPrice(item).isPositive();
}

export function averageWeight(item: ItemSummary): BigNumber {
  return new BigNumber(item.averageWeight);
}

export function discountAmount(item: ItemSummary): BigNumber {
  return suggestedPrice(item).minus(unitPrice(item));
}

export function isDiscounted(item: ItemSummary): boolean {
  return discountAmount(item).isGreaterThan(new BigNumber(0));
}

export function isDiscountedInCart(item: ItemSummary): boolean {
  return Boolean(item.autoDiscount || item.buyDown);
}

/**
 * Get the "each" count of an item sold by average weight given the quantity.
 */
export function getEachCountFromQuantity(item: ItemSummary, quantity: BigNumber) {
  if (!isSoldByAverageWeight(item)) {
    throw new Error('Item count is not defined for items not sold by average weight');
  }
  return quantity.dividedBy(averageWeight(item)).integerValue(BigNumber.ROUND_HALF_UP);
}
/**
 * Get weightedItemQuantity of an item. For random weight items, this is simply the weightedItemQuantity.
 * For average weight items, this is derived from calculating the "each".
 */
export function getWeightedItemQuantity(
  item: ItemSummary,
  quantity: BigNumber,
  weightedItemQuantity: BigNumber,
) {
  if (isSoldByAverageWeight(item)) {
    return getEachCountFromQuantity(item, quantity);
  }
  return weightedItemQuantity;
}

/**
 * Get the weight profile abbreviation for an item.
 * For average weight items, is '{abbrv} / ea avg'.
 * For random weight items, this is '{abbrv} / ea'.
 * For items with no weightedItemQuantity, this is {abbrv}.
 */
export function getItemAbbreviation(
  item: ItemSummary,
  quantity: BigNumber,
  weightedItemQuantity: BigNumber,
  translateFunc: Translate,
): string {
  const itemWeightedItemQuantity = getWeightedItemQuantity(item, quantity, weightedItemQuantity);
  if (itemWeightedItemQuantity && !itemWeightedItemQuantity.isNaN()) {
    const abbrv = item.weightProfile.abbrv;
    if (isSoldByAverageWeight(item)) {
      return translateFunc('weightPerEachAverage', { abbrv });
    }
    return translateFunc('weightPerEach', { abbrv });
  }
  return item.weightProfile.abbrv;
}

export function getStockBadge(item: ItemDetail, configuration: Configuration | undefined) {
  if (item.availability === PRODUCT_AVAILABILITY_STATUS.low) {
    return { icon: 'circle-info', text: configuration?.limitedAvailabilityTerm ?? '' };
  }
  if (item.availability === PRODUCT_AVAILABILITY_STATUS.order) {
    return { icon: 'circle-info', text: configuration?.onOrderTerm ?? '' };
  }
  if (item.availability === PRODUCT_AVAILABILITY_STATUS.showQuantity) {
    return { icon: 'circle-info', text: `${item.onHand} ${configuration?.inStockTerm ?? ''}` };
  }
  return null;
}
